/**
 * MorePage — Meer (spec §8.8). Replaces the previous placeholder menu:
 * every row navigates to a real route or performs a real action.
 *
 * - Profile card from useAuth() (server profile, never localStorage).
 * - Menu rows to Profiel, Privacy & beveiliging, Toegankelijkheid, Gegevens,
 *   App installeren, Meldingen, Hulp, Privacy & voorwaarden.
 * - Preference toggles via useUserSettings (optimistic + rollback + SyncStatus;
 *   display prefs apply immediately via <html> data-attributes).
 * - Storage status (local drafts, cloud configured, pending writes).
 * - Over RUST dialog with the canonical version, no medical claims.
 * - Uitloggen behind ConfirmDestructiveAction → signOut → /welcome.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Banner,
  Card,
  ConfirmDestructiveAction,
  Dialog,
  FeatureCard,
  StatusChip,
  SyncStatus,
  useToast,
} from '@/components/ui'
import {
  BellIcon,
  DownloadIcon,
  EyeIcon,
  HeartIcon,
  InfoIcon,
  LogoutIcon,
  NoteIcon,
  PlusIcon,
  ShieldIcon,
  UserIcon,
} from '@/components/icons'
import { PageHeader } from '@/components/navigation'
import { useAuth } from '@/features/auth'
import { isAppError } from '@/lib/supabase/errors'
import { PATHS } from '@/app/router/paths'
import { APP_VERSION } from '@/version'
import {
  DEFAULT_DISPLAY_PREFERENCES,
  applyDisplayPreferences,
} from '@/features/settings/applyPreferences'
import { PreferenceToggles } from '@/features/settings/PreferenceToggles'
import { StorageStatusCard } from '@/features/settings/StorageStatusCard'
import { getAvatarPublicUrl } from '@/features/settings/avatarUpload'
import { useUserSettings } from '@/features/settings/useUserSettings'
import '@/features/settings/settings.css'

const ROLE_LABEL: Record<string, string> = {
  client: 'Cliënt',
  caregiver: 'Begeleider',
  admin: 'Beheerder',
}

export default function MorePage() {
  const { email, profile, role, signOut } = useAuth()
  const { showToast } = useToast()
  const navigate = useNavigate()
  const settings = useUserSettings()

  const [aboutOpen, setAboutOpen] = useState(false)
  const [logoutOpen, setLogoutOpen] = useState(false)
  const [signingOut, setSigningOut] = useState(false)

  const displayName = profile?.displayName?.trim() || email || 'Gebruiker'
  const avatarUrl = getAvatarPublicUrl(profile?.avatarPath ?? null)

  const handleSignOut = async () => {
    setSigningOut(true)
    try {
      await signOut()
      // Reset any device-applied display prefs for the next session's load.
      applyDisplayPreferences(DEFAULT_DISPLAY_PREFERENCES)
      navigate(PATHS.welcome, { replace: true })
    } catch (error) {
      setSigningOut(false)
      setLogoutOpen(false)
      showToast({
        message: isAppError(error) ? error.userMessage : 'Uitloggen is niet gelukt. Probeer het opnieuw.',
        tone: 'error',
      })
    }
  }

  return (
    <div className="settings-page">
      <PageHeader title="Meer" subtitle={displayName} />

      {/* Profile card */}
      <Card onClick={() => navigate(PATHS.app.profile)}>
        <div className="profile-card">
          {avatarUrl ? (
            <img className="profile-card__avatar" src={avatarUrl} alt="" />
          ) : (
            <span className="profile-card__avatar" aria-hidden="true">
              <UserIcon size={30} />
            </span>
          )}
          <div className="profile-card__text">
            <p className="profile-card__name">{displayName}</p>
            {email && <p className="profile-card__meta">{email}</p>}
            {role && (
              <span className="profile-card__role">
                <StatusChip status="neutral">{ROLE_LABEL[role] ?? role}</StatusChip>
              </span>
            )}
          </div>
        </div>
      </Card>

      {/* Menu */}
      <section className="settings-section" aria-label="Instellingen">
        <h2 className="settings-section__title">Instellingen</h2>
        <div className="settings-card-list">
          <FeatureCard
            icon={<UserIcon size={22} />}
            title="Profiel"
            description="Naam, taal, tijdzone en profielfoto"
            to={PATHS.app.profile}
          />
          <FeatureCard
            icon={<ShieldIcon size={22} />}
            title="Privacy &amp; beveiliging"
            description="Wachtwoord wijzigen en sessie-informatie"
            to={PATHS.app.security}
          />
          <FeatureCard
            icon={<EyeIcon size={22} />}
            title="Toegankelijkheid"
            description="Beweging, contrast en tekstgrootte"
            to={PATHS.app.accessibility}
          />
          <FeatureCard
            icon={<DownloadIcon size={22} />}
            title="Gegevens"
            description="Exporteren, oude gegevens importeren, account verwijderen"
            to={PATHS.app.data}
          />
          <FeatureCard
            icon={<PlusIcon size={22} />}
            title="App installeren"
            description="Zet RUST op je beginscherm"
            to={PATHS.app.install}
          />
          <FeatureCard
            icon={<BellIcon size={22} />}
            title="Meldingen"
            description="Bekijk en beheer je meldingen"
            to={PATHS.app.notifications}
          />
        </div>
      </section>

      {/* Preferences */}
      <section className="settings-section" aria-label="Voorkeuren">
        <h2 className="settings-section__title">Voorkeuren</h2>
        <Card>
          {settings.loading ? (
            <p className="settings-loading">Voorkeuren laden…</p>
          ) : (
            <PreferenceToggles values={settings.values} update={settings.update} />
          )}
          <div className="settings-sync-row" aria-live="polite">
            <SyncStatus state={settings.syncState} />
          </div>
          {!settings.cloudAvailable && !settings.loading && (
            <Banner tone="info">
              De cloudverbinding is niet beschikbaar. Je voorkeuren worden alleen op dit apparaat
              bewaard.
            </Banner>
          )}
          {settings.error && <p className="settings-inline-error">{settings.error.userMessage}</p>}
        </Card>
      </section>

      {/* Storage status */}
      <section className="settings-section" aria-label="Opslag">
        <h2 className="settings-section__title">Opslag</h2>
        <StorageStatusCard />
      </section>

      {/* Support & legal */}
      <section className="settings-section" aria-label="Hulp en informatie">
        <h2 className="settings-section__title">Hulp en informatie</h2>
        <div className="settings-card-list">
          <FeatureCard
            icon={<InfoIcon size={22} />}
            title="Hulp &amp; ondersteuning"
            description="Veelgestelde vragen en contact"
            to={PATHS.legal.help}
          />
          <FeatureCard
            icon={<ShieldIcon size={22} />}
            title="Privacybeleid"
            description="Hoe RUST met je gegevens omgaat"
            to={PATHS.legal.privacy}
          />
          <FeatureCard
            icon={<NoteIcon size={22} />}
            title="Gebruiksvoorwaarden"
            description="De afspraken voor het gebruik van RUST"
            to={PATHS.legal.terms}
          />
          <FeatureCard
            icon={<HeartIcon size={22} />}
            title="Over RUST"
            description={`RUST v${APP_VERSION}`}
            onClick={() => setAboutOpen(true)}
          />
        </div>
      </section>

      {/* Logout */}
      <section className="settings-section" aria-label="Account">
        <div className="settings-card-list">
          <FeatureCard
            icon={<LogoutIcon size={22} />}
            title="Uitloggen"
            description="Je gegevens blijven bewaard"
            onClick={() => setLogoutOpen(true)}
          />
        </div>
      </section>

      {/* About dialog */}
      <Dialog open={aboutOpen} onClose={() => setAboutOpen(false)} title="Over RUST">
        <p className="about-dialog__version">{`RUST v${APP_VERSION}`}</p>
        <p className="about-dialog__body">
          RUST helpt je dag structureren met een planner, herinneringen, ademhalingsoefeningen, een
          dagboek en vaste plekken voor je spullen. Cliënten kunnen hun gegevens delen met een
          begeleider. RUST is geen medisch hulpmiddel en geeft geen medisch advies; je gegevens
          blijven van jou en zijn te exporteren via Gegevens.
        </p>
      </Dialog>

      {/* Logout confirmation */}
      <ConfirmDestructiveAction
        open={logoutOpen && !signingOut}
        title="Uitloggen?"
        body="Je wordt uitgelogd op dit apparaat. Je gegevens in de cloud en lokale concepten blijven bewaard."
        confirmLabel="Uitloggen"
        onConfirm={() => void handleSignOut()}
        onCancel={() => setLogoutOpen(false)}
      />
      {signingOut && (
        <p className="settings-loading" role="status">
          Bezig met uitloggen…
        </p>
      )}
    </div>
  )
}
